import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {getClaimValue} from "../../utils/authUser";
import LimeClaimTypes from "../../models/limeClaimTypes";
import {composeUrl} from "../../utils/api";
import {MemberFileDetails} from "./Review";
import {useAuth} from "react-oidc-context";
import {MemberFileStatusId} from "./Members";
import {formatViewDateAndTimeShort} from "../../utils/dateFormat";
import LoadingSpinner from "../Shared/LoadingSpinner/LoadingSpinner";
import { saveAs } from 'file-saver';
import downloadIcon from "../../images/download.svg";
import checkCircle from "../../images/check-circle.svg";
import xCircle from "../../images/x-circle.svg";
import alertCircle from "../../images/alert-circle.svg";
import refreshIcon from "../../images/refresh.svg";
import MemberFileChanges from "./MemberFileChanges";
import useInterval from "../Shared/Hooks/useInterval";
import { portalCardSurface, portalPageTitle, portalSecondaryPillButton } from "../../theme/portalLayout";

function Details() {
    const auth = useAuth();
    const params = useParams();
    const navigate = useNavigate();

    const [memberFileDetails, setMemberFileDetails] = useState<MemberFileDetails>();
    const [loading, setLoading] = useState<boolean>(true);
    const [intervalDelay] = useState(5000);
    const [shouldAutoRefresh, setShouldAutoRefresh] = useState(false);

    useInterval(() => refresh, shouldAutoRefresh ? intervalDelay : null);

    const reviewedStatuses: MemberFileStatusId[] = [MemberFileStatusId.Abandoned, MemberFileStatusId.Confirmed];

    const navigateToMembers = () => {
        navigate("/members");
    }

    const navigateToReview = () => {
        navigate(`/members/review/${memberFileDetails?.id}`);
    }

    function refresh() {
        let memberFileId = params["memberFileId"];
        let orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
        fetch(composeUrl(`organisations/${orgUuid}/memberFiles/${memberFileId}`),
          {
              headers: [[ 'Authorization',  `Bearer ${auth.user?.access_token}` ]],
              method: 'GET'
          })
          .then(response => response.json() as Promise<MemberFileDetails>)
          .then(data => {
              setMemberFileDetails(data);
              setLoading(false);
              setShouldAutoRefresh(
                data.statusId === MemberFileStatusId.New ||
                data.statusId === MemberFileStatusId.Processing ||
                data.statusId === MemberFileStatusId.Confirming);
          });
    }

    React.useEffect(() => refresh(), [params]);

    const getStatusIcon = (statusId: MemberFileStatusId) => {
        switch (statusId) {
            case MemberFileStatusId.New:
            case MemberFileStatusId.Processing:
            case MemberFileStatusId.Confirming:
                return <img src={refreshIcon} alt="Confirmed" className="h-lg mr-md animate-spin"/>
            case MemberFileStatusId.Confirmed:
                return <img src={checkCircle} alt="Confirmed" className="h-lg mr-md"/>;
            case MemberFileStatusId.Abandoned:
                return <img src={xCircle} alt="Abandoned" className="h-lg mr-md"/>
            case MemberFileStatusId.Error:
                return <img src={alertCircle} alt="Error" className="h-lg mr-md"/>
            default:
                break;
        }
    }

    const downloadMemberFile = (filename: string) => {
        let memberFileId = params["memberFileId"];
        let orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
        fetch(composeUrl(`organisations/${orgUuid}/memberFiles/${memberFileId}/content`),
            {
                headers: [[ 'Authorization',  `Bearer ${auth.user?.access_token}` ]],
                method: 'GET'
            })
            .then(response => response.blob())
            .then(blob => {
                saveAs(blob, filename);
            });
    }

    return (
        <div>
            <div className="items-start mb-xl">
                <h1 className={`mb-sm ${portalPageTitle}`}>Members</h1>
                <h3 className="font-sans-co font-bold text-portal-teal text-h3 mb-md">File details</h3>
                <LoadingSpinner loading={loading}>
                    { memberFileDetails &&
                        <div className={`w-full max-w-2xl p-lg mb-xl text-portal-teal ${portalCardSurface}`}>
                            <table>
                                <tbody>
                                    <tr className="h-2xl">
                                        <td className="w-[8rem] text-p1"><b>Name</b></td>
                                        <td>
                                            <div className="grow flex flex-row items-center">
                                                <div className="mr-xl">
                                                    {memberFileDetails.filename}
                                                </div>
                                                <button
                                                    type="button"
                                                    className={`${portalSecondaryPillButton} py-xs`}
                                                    onClick={() => downloadMemberFile(memberFileDetails?.filename)}>
                                                    <div className="flex flex-row items-center justify-between font-normal">
                                                        <img src={downloadIcon} alt="Download_Icon" className="mr-sm h-md" />
                                                        <div className="ml-sm">
                                                            Download
                                                        </div>
                                                    </div>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="h-2xl text-p1">
                                        <td className="w-[8rem]"><b>Uploaded</b></td>
                                        <td>{`${formatViewDateAndTimeShort(memberFileDetails.uploadedOn)} by ${memberFileDetails.uploadedByUser}`}</td>
                                    </tr>
                                    {
                                        reviewedStatuses.includes(memberFileDetails.statusId) &&
                                        <tr className="h-2xl text-p1">
                                            <td className="w-[8rem]"><b>Reviewed</b></td>
                                            <td>{`${formatViewDateAndTimeShort(memberFileDetails.reviewedOn)} by ${memberFileDetails.reviewedByUser}`}</td>
                                        </tr>
                                    }
                                    <tr className="h-2xl">
                                        <td className="w-[8rem] text-p1"><b>Status</b></td>
                                        <td>
                                            <div className="grow flex flex-row items-center">
                                                {getStatusIcon(memberFileDetails.statusId)}
                                                {MemberFileStatusId[memberFileDetails.statusId]}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    }
                    {
                        memberFileDetails &&
                        reviewedStatuses.includes(memberFileDetails.statusId) &&
                        <div className="mb-md">
                            <h3 className="mb-lg font-sans-co font-bold text-portal-teal text-h3">{`${MemberFileStatusId[memberFileDetails.statusId]} changes`}</h3>
                            <MemberFileChanges memberFileDetails={memberFileDetails}/>
                        </div>
                    }
                    {
                        memberFileDetails &&
                        memberFileDetails.statusId === MemberFileStatusId.Error &&
                        <div className="mb-md">
                            <h3 className="mb-md font-sans-co font-bold text-portal-teal text-h3">File errors ({memberFileDetails.errors.length})</h3>
                            <div className={portalCardSurface}>
                            <table className="w-full">
                                <tbody>
                                {
                                    memberFileDetails.errors.map((err, i) =>
                                        <tr className="text-left text-portal-teal odd:bg-pure-white even:bg-portal-header-wash/40 h-lg" key={i}>
                                            <td className="p-sm border-b border-portal-teal/10">
                                                {err.message}
                                            </td>
                                        </tr>)
                                }
                                </tbody>
                            </table>
                            </div>
                        </div>
                    }
                    <button
                        type="button"
                        className={`${portalSecondaryPillButton} mr-md`}
                        onClick={navigateToMembers}>
                        Go back
                    </button>
                    {
                        memberFileDetails &&
                        memberFileDetails.statusId === MemberFileStatusId.Review &&
                        <button type="button" className="font-sans-co text-pure-white bg-dark-grey border border-dark-grey rounded-full px-lg py-sm font-bold ml-sm"
                            onClick={navigateToReview}>
                            Review changes
                        </button>
                    }
                </LoadingSpinner>
            </div>
        </div>
    );
}

export default Details;